import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import {
  CheckIcon,
  PencilIcon,
  XCircleIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Drawer } from '../../components/ui/Drawer';
import EditHistoryLink from '../../components/tablemaint/EditHistoryLink';
import dispatchRemovalService, {
  DispatchRemoveCategory,
  DispatchRemoveReason,
} from '../../services/dispatchRemoval.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

type RenameAction = 'confirm' | 'create-new' | 'cancel';

interface RenameConfirmState {
  type: 'category' | 'reason';
  id: number;
  categoryId?: number;
  oldName: string;
  newName: string;
  has_subcategories?: boolean;
  count: number;
}

interface CategoryFormData {
  name: string;
  has_subcategories: boolean;
}

interface ReasonFormData {
  name: string;
}

const DEFAULT_CATEGORY_FORM: CategoryFormData = { name: '', has_subcategories: false };

const STATUS_STYLES = {
  Active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  Inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

const DrawerErrorBanner: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
    <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-red-700 dark:text-red-300 flex-1">{message}</p>
    <button onClick={onDismiss} className="text-red-400 hover:text-red-600 dark:hover:text-red-200 flex-shrink-0">
      <span className="sr-only">Dismiss</span>
      <XCircleIcon className="w-4 h-4" />
    </button>
  </div>
);

export default function DispatchRemovalConfig() {
  const [categories, setCategories] = useState<DispatchRemoveCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  // Add Drawer
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<CategoryFormData>(DEFAULT_CATEGORY_FORM);
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [addDrawerError, setAddDrawerError] = useState<string | null>(null);

  // Edit Drawer
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DispatchRemoveCategory | null>(null);
  const [editFormData, setEditFormData] = useState<CategoryFormData>(DEFAULT_CATEGORY_FORM);
  const [editReasons, setEditReasons] = useState<DispatchRemoveReason[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editDrawerError, setEditDrawerError] = useState<string | null>(null);

  // Reason editing within drawer
  const [editingReasonId, setEditingReasonId] = useState<number | null>(null);
  const [reasonForm, setReasonForm] = useState<ReasonFormData>({ name: '' });
  const [newReasonName, setNewReasonName] = useState('');

  // Rename confirmation modal
  const [renameConfirm, setRenameConfirm] = useState<RenameConfirmState | null>(null);
  const {
    panelRef: renamePanelRef,
    panelStyle: renamePanelStyle,
    dragHandleProps: renameDragHandleProps,
  } = useModalDrag(renameConfirm !== null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatchRemovalService.getCategories(true);
      setCategories(data);
    } catch {
      setErrorNotification({ title: 'Failed to Load', message: 'Could not load dispatch removal categories.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---- Table columns ----
  const handleOpenEditDrawer = useCallback((cat: DispatchRemoveCategory) => {
    setEditingCategory(cat);
    setEditFormData({ name: cat.name, has_subcategories: cat.has_subcategories });
    setEditReasons([...cat.reasons]);
    setEditDrawerError(null);
    setEditingReasonId(null);
    setReasonForm({ name: '' });
    setNewReasonName('');
    setIsEditDrawerOpen(true);
  }, []);

  const handleToggleActive = useCallback(async (cat: DispatchRemoveCategory) => {
    try {
      await dispatchRemovalService.updateCategory(cat.id, { is_active: !cat.is_active });
      setSuccessNotification({
        title: cat.is_active ? 'Deactivated' : 'Activated',
        message: `Category '${cat.name}' has been ${cat.is_active ? 'deactivated' : 'activated'}.`,
      });
      loadData();
    } catch {
      setErrorNotification({ title: 'Error', message: 'Could not update category status.' });
    }
  }, [loadData]);

  const columns = useMemo<ColumnDef<DispatchRemoveCategory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Category Name',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 250,
        cell: ({ row }) => (
          <span className="font-semibold text-blue-600 dark:text-blue-400">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'has_subcategories',
        header: 'Has Reasons',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        accessorFn: (row) => row.has_subcategories ? 'Yes' : 'No',
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              row.original.has_subcategories
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {row.original.has_subcategories ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        id: 'reasons',
        header: 'Reasons',
        enableSorting: false,
        enableColumnFilter: false,
        size: 300,
        cell: ({ row }) => {
          const cat = row.original;
          if (!cat.has_subcategories) {
            return <span className="text-gray-400 dark:text-gray-500 text-sm">N/A</span>;
          }
          const activeReasons = cat.reasons.filter(r => r.is_active);
          if (!activeReasons.length) {
            return <span className="text-gray-400 dark:text-gray-500 text-sm">None</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {activeReasons.map(r => (
                <span
                  key={r.id}
                  className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-label rounded"
                >
                  {r.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        accessorFn: (row) => row.is_active ? 'Active' : 'Inactive',
        cell: ({ row }) => {
          const status = row.original.is_active ? 'Active' : 'Inactive';
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
              {status}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 130,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const cat = row.original;
          return (
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => handleOpenEditDrawer(cat)}
                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="Edit Category"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleToggleActive(cat)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  cat.is_active
                    ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                    : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
                title={cat.is_active ? 'Deactivate' : 'Activate'}
              >
                {cat.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          );
        },
      },
    ],
    [handleOpenEditDrawer, handleToggleActive]
  );

  // ---- Add Category ----
  const handleAddNew = async () => {
    if (!addFormData.name?.trim()) {
      setAddDrawerError('Category name is required.');
      return;
    }
    setIsSavingAdd(true);
    setAddDrawerError(null);
    try {
      await dispatchRemovalService.createCategory(addFormData);
      setSuccessNotification({ title: 'Added Successfully', message: `Category '${addFormData.name}' has been created.` });
      closeAddDrawer();
      loadData();
    } catch (error: any) {
      setAddDrawerError(error?.response?.data?.detail || error?.message || 'Error creating category');
    } finally {
      setIsSavingAdd(false);
    }
  };

  const closeAddDrawer = () => {
    setIsAddDrawerOpen(false);
    setAddFormData(DEFAULT_CATEGORY_FORM);
    setAddDrawerError(null);
  };

  // ---- Edit Category (with reasons management) ----
  const handleSaveEdit = async () => {
    if (!editingCategory) return;
    if (!editFormData.name?.trim()) {
      setEditDrawerError('Category name is required.');
      return;
    }

    setIsSavingEdit(true);
    setEditDrawerError(null);

    try {
      const nameChanged = editFormData.name !== editingCategory.name;
      if (nameChanged) {
        const { count } = await dispatchRemovalService.getCategoryUsageCount(editingCategory.id);
        if (count > 0) {
          setIsEditDrawerOpen(false);
          setRenameConfirm({
            type: 'category',
            id: editingCategory.id,
            oldName: editingCategory.name,
            newName: editFormData.name,
            has_subcategories: editFormData.has_subcategories,
            count,
          });
          setIsSavingEdit(false);
          return;
        }
      }
      await dispatchRemovalService.updateCategory(editingCategory.id, editFormData);
      setSuccessNotification({ title: 'Updated Successfully', message: `Category '${editFormData.name}' has been updated.` });
      closeEditDrawer();
      loadData();
    } catch (error: any) {
      setEditDrawerError(error?.response?.data?.detail || error?.message || 'Error saving category');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditingCategory(null);
    setEditFormData(DEFAULT_CATEGORY_FORM);
    setEditReasons([]);
    setEditDrawerError(null);
    setEditingReasonId(null);
    setNewReasonName('');
  };

  // ---- Reason CRUD within drawer ----
  const handleAddReason = async () => {
    if (!editingCategory || !newReasonName.trim()) return;
    try {
      await dispatchRemovalService.createReason(editingCategory.id, { name: newReasonName.trim() });
      setNewReasonName('');
      // Reload to get the new reason with its ID
      const data = await dispatchRemovalService.getCategories(true);
      const updated = data.find(c => c.id === editingCategory.id);
      if (updated) {
        setEditReasons([...updated.reasons]);
        setEditingCategory(updated);
      }
      setSuccessNotification({ title: 'Reason Added', message: `Reason '${newReasonName.trim()}' has been created.` });
    } catch (error: any) {
      setEditDrawerError(error?.response?.data?.detail || error?.message || 'Error adding reason');
    }
  };

  const handleStartEditReason = (reason: DispatchRemoveReason) => {
    setEditingReasonId(reason.id);
    setReasonForm({ name: reason.name });
  };

  const handleCancelEditReason = () => {
    setEditingReasonId(null);
    setReasonForm({ name: '' });
  };

  const handleSaveReason = async (reason: DispatchRemoveReason) => {
    if (!reasonForm.name.trim()) return;
    if (!editingCategory) return;

    try {
      const nameChanged = reasonForm.name !== reason.name;
      if (nameChanged) {
        const { count } = await dispatchRemovalService.getReasonUsageCount(reason.id);
        if (count > 0) {
          setIsEditDrawerOpen(false);
          setRenameConfirm({
            type: 'reason',
            id: reason.id,
            categoryId: editingCategory.id,
            oldName: reason.name,
            newName: reasonForm.name,
            count,
          });
          setEditingReasonId(null);
          return;
        }
      }
      await dispatchRemovalService.updateReason(reason.id, { name: reasonForm.name });
      setEditReasons(prev => prev.map(r => r.id === reason.id ? { ...r, name: reasonForm.name } : r));
      setEditingReasonId(null);
      setSuccessNotification({ title: 'Reason Updated', message: `Reason has been updated.` });
    } catch (error: any) {
      setEditDrawerError(error?.response?.data?.detail || error?.message || 'Error saving reason');
    }
  };

  const handleToggleReasonActive = async (reason: DispatchRemoveReason) => {
    try {
      await dispatchRemovalService.updateReason(reason.id, { is_active: !reason.is_active });
      setEditReasons(prev => prev.map(r => r.id === reason.id ? { ...r, is_active: !r.is_active } : r));
      setSuccessNotification({
        title: reason.is_active ? 'Reason Deactivated' : 'Reason Activated',
        message: `Reason '${reason.name}' has been ${reason.is_active ? 'deactivated' : 'activated'}.`,
      });
    } catch {
      setEditDrawerError('Error updating reason status.');
    }
  };

  // ---- Rename confirmation ----
  const handleRenameAction = async (action: RenameAction) => {
    if (!renameConfirm) return;
    const { type, id, categoryId, newName, has_subcategories } = renameConfirm;

    try {
      if (action === 'confirm') {
        if (type === 'category') {
          await dispatchRemovalService.updateCategory(id, { name: newName, has_subcategories });
        } else {
          await dispatchRemovalService.updateReason(id, { name: newName });
        }
        setSuccessNotification({ title: 'Renamed', message: `${type === 'category' ? 'Category' : 'Reason'} has been renamed.` });
      } else if (action === 'create-new') {
        if (type === 'category') {
          await dispatchRemovalService.updateCategory(id, { is_active: false });
          await dispatchRemovalService.createCategory({ name: newName, has_subcategories: has_subcategories ?? false });
        } else {
          await dispatchRemovalService.updateReason(id, { is_active: false });
          await dispatchRemovalService.createReason(categoryId!, { name: newName });
        }
        setSuccessNotification({ title: 'Created', message: `Old ${type} deactivated, new one created.` });
      }
    } catch (error: any) {
      setErrorNotification({ title: 'Error', message: error?.response?.data?.detail || 'Error processing rename' });
    }

    setRenameConfirm(null);
    if (action !== 'cancel') {
      loadData();
    }
  };

  return (
    <div className="h-full w-full p-10 pb-24">
      <TanTable
        data={categories}
        columns={columns}
        loading={loading}
        settingsKey="dispatch-removal-config-table"
        fieldName="dispatch-removal-config-table"
        searchPlaceholder="Search categories..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Dispatch Removal Reasons"
        onEditFilters={() => setIsAddDrawerOpen(true)}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add Category"
        lockedColumns={[{ id: 'actions', side: 'right' }]}
      />

      {/* Add Drawer */}
      <Drawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        title="Add New Category"
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeAddDrawer}
              disabled={isSavingAdd}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNew}
              disabled={isSavingAdd}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSavingAdd ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Add Category
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          {addDrawerError && <DrawerErrorBanner message={addDrawerError} onDismiss={() => setAddDrawerError(null)} />}

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={addFormData.name}
              onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
              placeholder="e.g., Equipment Issues, Driver Request"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={isSavingAdd}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add_has_subcategories"
              checked={addFormData.has_subcategories}
              onChange={(e) => setAddFormData({ ...addFormData, has_subcategories: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600"
              disabled={isSavingAdd}
            />
            <label htmlFor="add_has_subcategories" className="text-sm text-label">
              Has subcategories (reasons)
            </label>
          </div>
          <p className="text-xs text-muted -mt-4">
            If checked, you can add specific reasons under this category after saving.
          </p>
        </div>
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        title="Edit Category"
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeEditDrawer}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSavingEdit ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          {editingCategory && (
            <div className="flex justify-end">
              <EditHistoryLink endpoint={`/dispatch-removal/categories/${editingCategory.id}/history`} label={editingCategory.name} />
            </div>
          )}
          {editDrawerError && <DrawerErrorBanner message={editDrawerError} onDismiss={() => setEditDrawerError(null)} />}

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              placeholder="e.g., Equipment Issues, Driver Request"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={isSavingEdit}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit_has_subcategories"
              checked={editFormData.has_subcategories}
              onChange={(e) => setEditFormData({ ...editFormData, has_subcategories: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600"
              disabled={isSavingEdit}
            />
            <label htmlFor="edit_has_subcategories" className="text-sm text-label">
              Has subcategories (reasons)
            </label>
          </div>

          {/* Reasons management section */}
          {editFormData.has_subcategories && editingCategory && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-body mb-3">Reasons</h3>

              {/* Existing reasons */}
              <div className="space-y-2 mb-3">
                {editReasons.map(reason => (
                  <div
                    key={reason.id}
                    className={`flex items-center justify-between p-2 rounded-lg border border-gray-200 dark:border-gray-700 ${
                      !reason.is_active ? 'opacity-60' : ''
                    }`}
                  >
                    {editingReasonId === reason.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={reasonForm.name}
                          onChange={(e) => setReasonForm({ name: e.target.value })}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveReason(reason);
                            if (e.key === 'Escape') handleCancelEditReason();
                          }}
                        />
                        <button
                          onClick={() => handleSaveReason(reason)}
                          className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          title="Save"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEditReason}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Cancel"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-label">{reason.name}</span>
                          {!reason.is_active && (
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-muted px-2 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEditReason(reason)}
                            className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                            title="Edit Reason"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleToggleReasonActive(reason)}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              reason.is_active
                                ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                                : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                            }`}
                          >
                            {reason.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {editReasons.length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 py-2">No reasons yet.</p>
                )}
              </div>

              {/* Add new reason */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newReasonName}
                  onChange={(e) => setNewReasonName(e.target.value)}
                  placeholder="New reason name..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddReason();
                  }}
                />
                <button
                  onClick={handleAddReason}
                  disabled={!newReasonName.trim()}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* Rename Confirmation Modal — portaled to body so it renders above the Drawer */}
      {renameConfirm && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-scrim/50">
          <div
            ref={renamePanelRef}
            style={renamePanelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full mx-4"
          >
            <ModalHeader
              title={
                <span className="flex items-center gap-3">
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
                  Historical Records Exist
                </span>
              }
              onClose={() => handleRenameAction('cancel')}
              dragHandleProps={renameDragHandleProps}
            />
            <div className="px-6 py-4">
              <p className="text-sm text-label">
                There {renameConfirm.count === 1 ? 'is' : 'are'}{' '}
                <span className="font-semibold">{renameConfirm.count}</span> historical removal
                {renameConfirm.count === 1 ? ' record' : ' records'} using this{' '}
                {renameConfirm.type}. Are you sure you want to rename{' '}
                <span className="font-semibold">"{renameConfirm.oldName}"</span> to{' '}
                <span className="font-semibold">"{renameConfirm.newName}"</span>?
              </p>
              <div className="mt-4 space-y-3">
                <button
                  onClick={() => handleRenameAction('confirm')}
                  className="w-full text-left px-4 py-3 rounded-lg border border-rule hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="text-sm font-medium text-body">Rename anyway</div>
                  <div className="text-xs text-muted mt-0.5">
                    All historical records will reflect the new name.
                  </div>
                </button>
                <button
                  onClick={() => handleRenameAction('create-new')}
                  className="w-full text-left px-4 py-3 rounded-lg border border-rule hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="text-sm font-medium text-body">
                    Create new &amp; deactivate old
                  </div>
                  <div className="text-xs text-muted mt-0.5">
                    Deactivate "{renameConfirm.oldName}" and create a new {renameConfirm.type} named "{renameConfirm.newName}". Historical records keep the old name.
                  </div>
                </button>
                <button
                  onClick={() => handleRenameAction('cancel')}
                  className="w-full text-left px-4 py-3 rounded-lg border border-rule hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="text-sm font-medium text-body">Cancel</div>
                  <div className="text-xs text-muted mt-0.5">
                    Keep the current name unchanged.
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Notifications */}
      {errorNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>,
        document.body
      )}

      {successNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>,
        document.body
      )}
    </div>
  );
}
